/**
 * Theme preference — the persistence primitive behind `ThemeProvider`
 * (roadmap #11). Kept as a small, dependency-free module so the storage
 * mechanism can be swapped without touching the provider/consumers.
 *
 * `design-system.md` §2.1 describes the target shape: "theme choice is
 * stored in the user's preference record (server-side, so it follows the
 * user across devices) and mirrored to `localStorage` for first-paint".
 *
 * IB-292 (server-sync delta, 2026-08-27): that per-user preference endpoint
 * now exists (`GET/PUT /api/v1/me/preferences/theme`, `api/userPreferences.ts`)
 * and `ThemeProvider` hydrates from / best-effort PUTs to it once
 * authenticated. This module itself stays `localStorage`-only BY DESIGN —
 * it is the FAST, synchronous first-paint source `ThemeProvider` and the
 * inline bootstrap script in `index.html` both need before any network
 * round trip could possibly resolve; the server round trip only ever
 * OVERRIDES it post-hydration when the two disagree (`ThemeProvider`'s own
 * effect), never blocks it.
 */

export type ThemePreference = 'light' | 'dark' | 'system'
export type EffectiveTheme = 'light' | 'dark'

export const THEME_STORAGE_KEY = 'iw.theme-preference'

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

/**
 * Reads the persisted preference. Never throws — a storage failure (private
 * browsing, disabled storage, quota) or an unset/corrupt value falls back to
 * `'light'`, the product's documented default for a brand-new user
 * (design-system.md §2.1: "Initial default for a brand-new user: Light,
 * never `prefers-color-scheme`" — the OS hint is only ever applied when the
 * user explicitly opts into the `system` preference below).
 */
export function readThemePreference(): ThemePreference {
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isThemePreference(raw) ? raw : 'light'
  } catch {
    return 'light'
  }
}

/** Persists the preference. Silently no-ops on storage failure (state still applies for the session). */
export function writeThemePreference(preference: ThemePreference): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preference)
  } catch {
    // Storage unavailable — the in-memory/React state still reflects the choice this session.
  }
}

/** `null` in non-browser/test environments without `matchMedia`. */
export function systemPrefersDarkMedia(): MediaQueryList | null {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null
  return window.matchMedia('(prefers-color-scheme: dark)')
}

export function systemPrefersDarkNow(): boolean {
  return systemPrefersDarkMedia()?.matches ?? false
}

/** Resolves a preference (which may defer to the OS) to a concrete, applicable theme. */
export function resolveEffectiveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
): EffectiveTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light'
  return preference
}
